// #region Global Imports
import React from "react";
import { Field, FastField, getIn } from "formik";
import { Form, Dropdown } from "react-bootstrap";
// #endregion Global Imports

// #region Local Imports
import { SelectFieldProps } from "./SelectField";
// #endregion Local Imports

const SelectField: React.FunctionComponent<SelectFieldProps> = ({
    name,
    className = "select-field",
    label,
    placeholder,
    hint,
    onChange,
    options = [],
    fastField = false,
    disabled = false,
    isRequired = false,
}): JSX.Element => {
    const render = ({
        field,
        form: { touched, errors, setFieldValue },
    }: {
        field: any;
        form: any;
    }) => {
        const error = getIn(errors, name);
        const isTouched = getIn(touched, name);

        const handleChange = (option: string | null) => {
            const value = option || "";

            setFieldValue(name, value);

            if (onChange) {
                onChange(value);
            }
        };

        const getSelectedOption = () =>
            options
                ? options.find((option) => option.value === field.value)
                : null;

        const isInvalid = isTouched && !!error;
        const selectedOption = getSelectedOption();

        return (
            <Form.Group controlId={name}>
                {label && (
                    <Form.Label>{isRequired ? `${label} *` : label}</Form.Label>
                )}
                <Dropdown
                    className={`${className} ${isInvalid ? "is-invalid" : ""}`}
                >
                    <Dropdown.Toggle
                        childBsPrefix={className}
                        disabled={disabled}
                    >
                        <div>
                            {selectedOption?.label ||
                                (isRequired ? `${placeholder} *` : placeholder)}
                        </div>
                        <i className="fa-solid fa-angle-down" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        {options.map((option) => (
                            <Dropdown.Item
                                key={option.value}
                                eventKey={option.value}
                                active={selectedOption?.value === option.value}
                                onSelect={handleChange}
                            >
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <Form.Text muted={true}>{hint}</Form.Text>
                <Form.Control.Feedback type="invalid">
                    {error}
                </Form.Control.Feedback>
            </Form.Group>
        );
    };

    if (fastField) {
        return <FastField name={name}>{render}</FastField>;
    }
    return <Field name={name}>{render}</Field>;
};

export { SelectField };
